import { chargeCustomerWithCloudTerminal } from "./pelecardCloud";
import { createInvoiceOnly } from "./pelecardInvoiceOnly";
import { supabase } from "./supabase";

type PaymentMethod = "credit_card" | "bank_transfer" | "bit" | "cash";

interface ChargeCustomerParams {
  businessId: string;
  customerId: string;
  appointmentId?: string;
  amount: number;
  tipAmount?: number;
  paymentMethod: PaymentMethod;
  serviceDescription: string;
  customerName: string;
  customerEmail: string;
  customerPhone?: string;
  customerAddress?: string;
  accessToken?: string;
  terminalId?: string;

  Uid?: string;
}

/**
 * ניחוש חכם האם העסק פטור/מורשה לפי נתוני businesses.
 * כדי לא להיתקע על שמות עמודות שונים, אנחנו קוראים select('*') ומפרשים.
 * ברירת מחדל: מורשה (includeVat=true) כדי לא לשבור זרימות קיימות.
 */
async function resolveIncludeVatForBusiness(businessId: string): Promise<boolean> {
  const { data: biz, error } = await supabase
    .from("businesses")
    .select("*")
    .eq("id", businessId)
    .single();

  if (error) {
    // לא מפילים את כל התשלום בגלל זה - אבל מדפיסים כדי שתראה ותשפר
    console.warn("[payments] resolveIncludeVatForBusiness failed:", error.message);
    return true; // default = מורשה
  }

  if (!biz) return true;

  // נסיונות נפוצים
  const vatType = String(biz.vat_type ?? biz.vatType ?? "").toLowerCase().trim();
  const isExemptBool =
    biz.is_vat_exempt ??
    biz.vat_exempt ??
    biz.isVatExempt ??
    biz.vatExempt ??
    biz.exempt_vat ??
    biz.exemptVat;

  const vatRegistered =
    biz.vat_registered ?? biz.vatRegistered ?? biz.is_vat_registered ?? biz.isVatRegistered;

  const includeVatDefault =
    biz.include_vat_default ?? biz.includeVatDefault ?? biz.default_include_vat ?? biz.defaultIncludeVat;

  // אם יש עמודה מפורשת של includeVat ברירת מחדל - נשתמש בה
  if (typeof includeVatDefault === "boolean") return includeVatDefault;

  // אם יש עמודת פטור בוליאנית - היא מנצחת
  if (typeof isExemptBool === "boolean") return !isExemptBool;

  // אם כתוב exempt/פטור בטקסט
  if (vatType.includes("exempt") || vatType.includes("פטור")) return false;

  // אם כתוב licensed/registered/מורשה בטקסט
  if (vatType.includes("licensed") || vatType.includes("registered") || vatType.includes("מורשה")) return true;

  // אם יש vat_registered בוליאני
  if (typeof vatRegistered === "boolean") return vatRegistered;

  // fallback
  return true;
}

export async function chargeCustomer(params: ChargeCustomerParams) {
  // מסוף תשלום - אם נבחר מסוף ספציפי, משתמשים בו. אחרת מושכים את הראשון
  let terminal;

  console.log("🔍 chargeCustomer - params.terminalId:", params.terminalId);

  if (params.terminalId) {
    console.log("✅ שולף מסוף ספציפי:", params.terminalId);
    const { data: selectedTerminal, error: terminalError } = await supabase
      .from("payment_terminals")
      .select("*")
      .eq("id", params.terminalId)
      .eq("business_id", params.businessId)
      .eq("is_active", true)
      .single();

    if (terminalError) {
      console.error("❌ שגיאה בשליפת המסוף:", terminalError);
      throw new Error("שגיאה בשליפת המסוף שנבחר");
    }
    terminal = selectedTerminal;
    console.log("✅ נשלף מסוף:", terminal?.terminal_name, "ID:", terminal?.id);
  } else {
    console.log("⚠️ לא נשלח terminalId - משתמש במסוף ברירת מחדל");
    const { data: payment_terminals, error: terminalError } = await supabase
      .from("payment_terminals")
      .select("*")
      .eq("business_id", params.businessId)
      .eq("is_active", true)
      .order("created_at", { ascending: false })
      .limit(1);

    if (terminalError) {
      throw new Error("שגיאה בשליפת מסוף התשלום");
    }
    terminal = payment_terminals?.[0];
  }

  if (!terminal) {
    const { data: allTerminals } = await supabase
      .from("payment_terminals")
      .select("id, terminal_name, is_active, enable_invoice")
      .eq("business_id", params.businessId);

    if (allTerminals && allTerminals.length > 0) {
      const inactiveReasons = allTerminals
        .map((t) => `${t.terminal_name}: is_active=${t.is_active}, enable_invoice=${t.enable_invoice}`)
        .join("; ");
      throw new Error(`לא הוגדר מסוף פעיל למשתמש זה. מסופים קיימים: ${inactiveReasons}`);
    } else {
      throw new Error("לא נמצאו הגדרות מסוף תשלום לעסק זה. אנא הוסף מסוף בהגדרות.");
    }
  }

  if (!terminal.enable_invoice) {
    throw new Error("אין אפשרות להפיק חשבונית במסוף זה. אנא הפעל את האפשרות בהגדרות.");
  }

  if (params.paymentMethod === "credit_card" && !terminal.is_active) {
    throw new Error("אין אפשרות לחייב כרטיס אשראי בעסק זה.");
  }

  // סכום כולל
  const totalAmount = Number(params.amount) + Number(params.tipAmount || 0);

  // ✅ החלטה נכונה לפי העסק: פטור/מורשה
  const includeVatForBusiness = await resolveIncludeVatForBusiness(params.businessId);

  // תהליך לפי סוג תשלום
  if (params.paymentMethod === "credit_card") {
    // חיוב במסוף + הפקת חשבונית
    const result = await chargeCustomerWithCloudTerminal({
      businessId: params.businessId,
      customerId: params.customerId,
      appointmentId: params.appointmentId,
      amount: totalAmount,
      currency: 1,
      payments: 1,
      reference: params.appointmentId || Date.now().toString(),
      terminalId: terminal.id, // ✅ העברת מזהה המסוף
      payperParams: {
        typeDocument: "Invoice-Receipt",
        DataPayper: {
          customer_name: params.customerName,
          customer_mail: params.customerEmail,
          customer_phone: params.customerPhone,
          customer_address: params.customerAddress || "לא צויין",
          invoice_lines: [
            {
              description: params.serviceDescription,
              quantity: "1",
              price_per_unit: params.amount.toString(),
            },
            ...(params.tipAmount
              ? [
                  {
                    description: "טיפ",
                    quantity: "1",
                    price_per_unit: params.tipAmount.toString(),
                  },
                ]
              : []),
          ],
        },
      },
      sandbox: terminal.sandbox_mode ?? false,
      accessToken: params.accessToken,
      includeVat: true, // ✅ חשוב
    });

    if (!result.success) throw new Error(result.error || "החיוב נכשל, נסה שוב.");
    return result;
  }

  // תשלום שאינו אשראי: הפקת חשבונית בלבד
  const invoiceParams = {
    businessId: params.businessId,
    customerId: params.customerId,
    appointmentId: params.appointmentId,
    terminalId: terminal.id,
    amount: params.amount,
    tipAmount: params.tipAmount || 0,
    serviceDescription: params.serviceDescription,
    customerName: params.customerName,
    customerEmail: params.customerEmail || "noemail@example.com",
    customerPhone: params.customerPhone,
    customerAddress: params.customerAddress || "לא צויין",
    paymentType: params.paymentMethod || "cash",
    includeVat: true,
    documentRemarks: "תודה שבחרת בנו!",
    Uid: params.Uid || "",
  };

  const invoice = await createInvoiceOnly(invoiceParams, params.accessToken || "");

  // קישור לחשבונית
  const invoiceUrl =
    invoice.invoiceLink ||
    (invoice as any).document_link ||
    (invoice as any).raw?.document_link ||
    (invoice as any).raw?.invoice_link ||
    (invoice as any).raw?.receipt_link ||
    (invoice as any).raw?.result?.document_link ||
    (invoice as any).raw?.result?.invoice_link ||
    null;

  // שמירה ב-DB
  const { error } = await supabase.from("payment_transactions").insert([
    {
      business_id: params.businessId,
      customer_id: params.customerId,
      appointment_id: params.appointmentId || null,
      amount: totalAmount,
      currency: 1,
      payment_method: params.paymentMethod,
      invoice_link: invoiceUrl,
      status: "success",
    },
  ]);

  if (error) throw new Error("שמירת החשבונית נכשלה");

  return { ...invoice, invoiceLink: invoiceUrl };
}
